import { Alert, Box, Button, Fade, IconButton, Slider, Tooltip, Typography } from "@mui/material";
import { PlayerEvent } from "../types";
import "./ReplayControls.css";
import { ArrowBack, ArrowForward, Link } from "@mui/icons-material";
import { ClickableSliderMarkLabel } from "../genericComponents";
import { useState } from "react";
import { isModKeyDown } from "src/components/utils";

export function ReplayControls(props: {
  moveToEvent: (eventNumber: number) => void;
  currEventNumber: number;
  allEvents: PlayerEvent[];
  createDebugTable: () => void;
  tableStatus: string;
}) {
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [localCurrentEventNumber, setLocalCurrentEventNumber] = useState<number | null>(null);

  let currEventNumber = props.currEventNumber;
  let eventAmt = props.allEvents.length;
  if (props.tableStatus !== "ended") {
    return null;
  }

  let marks = [];
  marks.push({ value: 0, label: "S" });
  props.allEvents
    .flatMap((move, moveId) => {
      let result =
        move.data?.replayMarks?.map((mark) => {
          return { value: moveId + 1, label: mark };
        }) ?? [];
      return result;
    })
    .forEach((mark) => marks.push(mark));
  marks.push({ value: eventAmt, label: "E" });

  function goToMove(val: number) {
    props.moveToEvent(val);
  }

  function sliderMarkOnClick(e: React.SyntheticEvent, markValue: string) {
    if (!Number.isNaN(+markValue)) {
      goToMove(+markValue);
    }
  }

  function createTurnLinkAndAddToClipboard() {
    setAlertVisible(true);
    var url = window.location.origin + window.location.pathname + "?turnNumber=" + props.currEventNumber;
    navigator.clipboard.writeText(url);
  }

  const hotkeysTooltip = (
    <div className="replay-hotkeys">
      <div className="centered">
        Hotkeys:&nbsp;<kbd>←</kbd>
        <kbd>→</kbd>
      </div>
      <div className="centered">
        Hold&nbsp;<kbd>Ctrl</kbd>/<kbd>Cmd</kbd>&nbsp;to step 10 moves at a time.
      </div>
    </div>
  );

  return (
    <div className="replay-controls">
      <div className="replay-turn-counter">
        <Typography variant="h6" width="152px" textAlign="center">{`Move ${currEventNumber}/${eventAmt}`}</Typography>
        <Tooltip title="Link to current turn" arrow placement="top">
          <IconButton color="primary" onClick={createTurnLinkAndAddToClipboard}>
            <Link />
          </IconButton>
        </Tooltip>
        <div className="replay-clipboard-alert-container">
          <Fade
            in={alertVisible}
            timeout={{ enter: 400, exit: 600 }}
            addEndListener={() => {
              setTimeout(() => {
                setAlertVisible(false);
              }, 1000);
            }}
          >
            <Alert severity="info" variant="standard">
              Copied to clipboard
            </Alert>
          </Fade>
        </div>
      </div>

      <div className="replay-debug-button-container">
        {localStorage.getItem("user-role")?.includes("admin") ? (
          <button style={{ fontSize: "10px" }} onClick={() => props.createDebugTable()}>
            Debug table at this position
          </button>
        ) : (
          <></>
        )}
      </div>

      <Tooltip title={hotkeysTooltip} arrow placement="top">
        <Box gap={1} display="flex">
          <Button
            variant="outlined"
            size="small"
            onClick={(evt) => goToMove(props.currEventNumber - (isModKeyDown(evt) ? 10 : 1))}
            disableRipple
          >
            <ArrowBack />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(evt) => goToMove(props.currEventNumber + (isModKeyDown(evt) ? 10 : 1))}
            disableRipple
          >
            <ArrowForward />
          </Button>
        </Box>
      </Tooltip>

      <div className="replay-slider-wrapper">
        <Slider
          step={1}
          min={0}
          max={eventAmt}
          value={localCurrentEventNumber ?? props.currEventNumber}
          valueLabelDisplay="auto"
          onChange={(evt, val) => setLocalCurrentEventNumber(val as number)}
          onChangeCommitted={(evt, val) => {
            goToMove(val as number);
            setLocalCurrentEventNumber(null);
          }}
          marks={marks}
          slotProps={{
            markLabel: { labelOnChange: sliderMarkOnClick } as any,
          }}
          slots={{
            markLabel: ClickableSliderMarkLabel,
          }}
          sx={{
            "& .MuiSlider-valueLabel": {
              right: "35px",
              top: "24px",
            },
            "& .MuiSlider-valueLabel::before": {
              bottom: "50%",
              left: "100%",
            },
          }}
        />
      </div>
    </div>
  );
}
